import { parseUtcIso } from "./parseUtcIso"
import type { DriverFuelRow, RefuelEvent } from "./fuelReportTypes"

export type FuelSummary = {
  refuels: number
  matched: number
  elsewhere: number
  alertsSent: number
  alertsNotSent: number
  seen: number
  notSeen: number
  hasSeenData: boolean
}

/** Aggregate stats for the summary card. `seen === undefined` means the
 * backend didn't provide seen data (old backend) — tracked via hasSeenData
 * so the card can show "—" instead of a misleading zero. */
export function computeFuelSummary(rows: DriverFuelRow[]): FuelSummary {
  const s: FuelSummary = {
    refuels: 0,
    matched: 0,
    elsewhere: 0,
    alertsSent: 0,
    alertsNotSent: 0,
    seen: 0,
    notSeen: 0,
    hasSeenData: false,
  }
  for (const row of rows) {
    for (const r of row.refuels) {
      s.refuels++
      if (r.verdict === "MATCH") s.matched++
      else s.elsewhere++
    }
    for (const a of row.alerts) {
      if (a.suppressed) {
        s.alertsNotSent++
        continue
      }
      s.alertsSent++
      if (a.seen === true) {
        s.seen++
        s.hasSeenData = true
      } else if (a.seen === false) {
        s.notSeen++
        s.hasSeenData = true
      }
    }
  }
  return s
}

export type ChipEvent = {
  tone: "green" | "red" | "gray"
  at: string
  refuel: RefuelEvent
}

/** One chip per REFUEL — refueling is the report's unit of story. Green:
 * refueled at the alerted station; red: had an alert, refueled elsewhere;
 * gray: refueled without any alert. Alerts never get chips of their own
 * (their aggregate lives in the summary card). */
export function driverChipEvents(row: DriverFuelRow): ChipEvent[] {
  return row.refuels
    .map((refuel) => ({
      tone:
        refuel.verdict === "MATCH"
          ? ("green" as const)
          : refuel.verdict === "ELSEWHERE" || refuel.verdict === "ERROR"
            ? ("red" as const)
            : ("gray" as const),
      at: refuel.refueledAt,
      refuel,
    }))
    .sort((a, b) => parseUtcIso(a.at).getTime() - parseUtcIso(b.at).getTime())
}

export type DriverStats = {
  refuels: number
  matched: number
  elsewhere: number
  matchedPct: number | null
  elsewherePct: number | null
}

export function driverStats(row: DriverFuelRow): DriverStats {
  const refuels = row.refuels.length
  const matched = row.refuels.filter((r) => r.verdict === "MATCH").length
  // Only refuels that had an alert and went elsewhere count as "elsewhere" —
  // a refuel with no alert at all (NO_SUGGESTION) is neutral, not red.
  const elsewhere = row.refuels.filter(
    (r) => r.verdict === "ELSEWHERE" || r.verdict === "ERROR",
  ).length
  return {
    refuels,
    matched,
    elsewhere,
    matchedPct: refuels ? (matched / refuels) * 100 : null,
    elsewherePct: refuels ? (elsewhere / refuels) * 100 : null,
  }
}

export function formatPct(pct: number | null): string {
  return pct === null ? "—" : `${pct.toFixed(1)}%`
}

/** Local-day bounds around an event — the detail dialog's telemetry window
 * stays a single day even when the report range spans weeks. */
export function eventDayWindow(iso: string): { from: string; to: string } {
  const d = parseUtcIso(iso)
  const from = new Date(d)
  from.setHours(0, 0, 0, 0)
  const to = new Date(d)
  to.setHours(23, 59, 59, 999)
  return { from: from.toISOString(), to: to.toISOString() }
}
